import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useChatStream } from '@/hooks/useChatStream';
import { csrfFetch } from '@/lib/csrf';
import { ChatInterface } from '@/components/chat/ChatInterface';
import { logWarn } from '@/lib/logger';

interface ChatSimulatorModalProps {
  patientId: number;
  patientName: string;
  trigger: React.ReactNode;
}

const INACTIVITY_TIMEOUT_MS = 5 * 60 * 1000;

function formatTimer(ms: number) {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

function buildSessionId() {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `sim-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
}

export default function ChatSimulatorModal({
  patientId,
  patientName,
  trigger,
}: ChatSimulatorModalProps) {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState<string>('');
  const sessionActiveRef = useRef(false);
  const sessionClosedRef = useRef(false);

  const closeSession = useCallback(
    async (reason: 'manual' | 'timeout') => {
      if (!sessionId || sessionClosedRef.current || !sessionActiveRef.current) return;
      sessionClosedRef.current = true;
      try {
        await csrfFetch(`/therapists/patients/${patientId}/simulations/end`, {
          method: 'POST',
          body: JSON.stringify({ session_id: sessionId, reason }),
        });
      } catch (err) {
        logWarn('Errore durante la chiusura della simulazione', err);
      } finally {
        sessionActiveRef.current = false;
      }
    },
    [patientId, sessionId],
  );

  const {
    input,
    setInput,
    msgList,
    loading,
    error,
    streamingMsg,
    bottomRef,
    inputRef,
    copiedId,
    handleCopy,
    handleSend,
    image,
    setImage,
    resetConversation,
    sessionExpired,
    remainingTimeMs,
    endSessionManually,
  } = useChatStream({
    streamUrl: `/therapists/patients/${patientId}/simulations/stream`,
    initialMessages: [],
    mode: 'ephemeral',
    sessionId,
    inactivityTimeoutMs: INACTIVITY_TIMEOUT_MS,
    onSessionTimeout: () => {
      void closeSession('timeout');
    },
    onEvent: (event) => {
      if (event.type === 'request:start') {
        sessionActiveRef.current = true;
        sessionClosedRef.current = false;
      }
    },
  });

  const startSession = useCallback(() => {
    if (sessionActiveRef.current && !sessionClosedRef.current) {
      void closeSession('manual');
    }
    const nextId = buildSessionId();
    setSessionId(nextId);
    resetConversation([]);
    setInput('');
    setImage(null);
    sessionActiveRef.current = false;
    sessionClosedRef.current = false;
  }, [closeSession, resetConversation, setImage, setInput]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen) {
        setOpen(true);
        startSession();
      } else {
        setOpen(false);
        void closeSession('manual');
        endSessionManually();
        resetConversation([]);
      }
    },
    [closeSession, endSessionManually, resetConversation, startSession],
  );

  const timerLabel = useMemo(() => formatTimer(remainingTimeMs), [remainingTimeMs]);

  const headerActions = (
    <div className="flex items-center gap-2">
      <Badge variant={sessionExpired ? 'destructive' : 'outline'}>
        {sessionExpired ? 'Sessione scaduta' : `Inattività: ${timerLabel}`}
      </Badge>
      {sessionExpired && (
        <Button variant="outline" size="sm" onClick={startSession} disabled={loading}>
          Nuova simulazione
        </Button>
      )}
    </div>
  );

  const infoBanner = (
    <div className="rounded-lg border border-primary/20 bg-primary/5 p-4 text-sm text-muted-foreground">
      <ul className="space-y-1 text-xs">
        <li>• Prova qui la chat come se fossi il cliente.</li>
        <li>• Nessun messaggio viene salvato o mostrato al cliente.</li>
        <li>• Il comportamento dell&apos;agente riflette le impostazioni attuali del cliente.</li>
      </ul>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {React.isValidElement(trigger) ? trigger : <span>{trigger}</span>}
      </DialogTrigger>
      <DialogContent className="w-[80vw] h-[80vh] max-w-7xl focus-visible:outline-none focus-visible:ring-0 flex flex-col overflow-hidden">
        <DialogHeader className="pb-2">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <DialogTitle>Test configurazione per {patientName}</DialogTitle>
              <DialogDescription>
                Conversazione temporanea con le impostazioni del cliente
              </DialogDescription>
            </div>
            {headerActions}
          </div>
        </DialogHeader>
        <div className="flex-1 min-h-0 mt-2">
          <ChatInterface
            input={input}
            setInput={setInput}
            msgList={msgList}
            loading={loading}
            error={error}
            streamingMsg={streamingMsg}
            bottomRef={bottomRef}
            inputRef={inputRef}
            copiedId={copiedId}
            handleCopy={handleCopy}
            handleSend={handleSend}
            image={image}
            setImage={setImage}
            allowAttachments={false}
            heightClass="h-full"
            headerActions={null}
            infoBanner={infoBanner}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
